/**
 * TanStack Query hooks — Form templates (F2-Forms-1c-i / 9.U.4).
 *
 * Consomme l'API privée broker livrée en 1b-i (templates + versions). Aucun
 * form-responses ici (1d). Types dérivés des schemas shared via `z.infer`
 * (source unique — pas de re-typage parallèle des objets API, cf. §13 PO).
 *
 * Note : les lignes `version` ne sont PAS re-validées runtime (parité
 * companies.ts : type-only). Le contrat `schema` d'une version est strict
 * côté publication mais permissif en brouillon — une validation `.parse`
 * côté web rejetterait à tort un brouillon partiel.
 *
 * Convention queryKey : ['form-templates', 'list'|'detail'|'versions', ...args].
 */

import type { DbPage } from "@kollectivia/shared";
import type { Schemas } from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { z } from "zod";

import { api } from "../api";

export type FormTemplateRow = z.infer<typeof Schemas.FormTemplateSchema>;
export type FormTemplateListItem = z.infer<typeof Schemas.FormTemplateListItemSchema>;
export type FormTemplateVersionRow = z.infer<typeof Schemas.FormTemplateVersionSchema>;

// Liste = contrat dédié enrichi de l'état de version (1c-iii, projection LATERAL).
export type FormTemplateListResult = DbPage<FormTemplateListItem>;

/** POST / retourne { template, draft_version } (create atomique 1b-i). */
export type CreateFormTemplateResult = {
  template: FormTemplateRow;
  draft_version: FormTemplateVersionRow;
};

/** GET /:id retourne { template, latest_version }. */
export type FormTemplateDetailResult = {
  template: FormTemplateRow;
  latest_version: FormTemplateVersionRow | null;
};

export type FormTemplateStatusFilter = "active" | "archived";

export type FormTemplateListParams = {
  cursor?: string | null;
  q?: string;
  status: FormTemplateStatusFilter;
};

function buildListPath(params: FormTemplateListParams): string {
  const sp = new URLSearchParams();
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.q) sp.set("q", params.q);
  // Le backend n'applique aucun statut par défaut → toujours l'envoyer.
  sp.set("status", params.status);
  return `/v1/form-templates?${sp.toString()}`;
}

export function useFormTemplates(params: FormTemplateListParams) {
  return useQuery({
    queryKey: ["form-templates", "list", params],
    queryFn: () => api.get<FormTemplateListResult>(buildListPath(params)),
    placeholderData: (prev) => prev,
  });
}

export function useFormTemplate(id: string | undefined) {
  return useQuery({
    queryKey: ["form-templates", "detail", id],
    queryFn: () => api.get<FormTemplateDetailResult>(`/v1/form-templates/${id}`),
    enabled: !!id,
  });
}

export function useFormTemplateVersions(id: string | undefined) {
  return useQuery({
    queryKey: ["form-templates", "versions", id],
    queryFn: () =>
      api.get<{ items: FormTemplateVersionRow[] }>(`/v1/form-templates/${id}/versions`),
    enabled: !!id,
  });
}

export function useCreateFormTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<CreateFormTemplateResult>("/v1/form-templates", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["form-templates", "list"] });
    },
  });
}

export function useUpdateFormTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.patch<FormTemplateRow>(`/v1/form-templates/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["form-templates", "list"] });
      void qc.invalidateQueries({ queryKey: ["form-templates", "detail", id] });
    },
  });
}

/**
 * Archive (DELETE = status='archived', soft, préserve versions — 1b-i). Pas de
 * hard delete exposé en UI.
 */
export function useArchiveFormTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<FormTemplateRow>(`/v1/form-templates/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["form-templates"] });
    },
  });
}

/**
 * Restaure un gabarit archivé — chunk 9.BJ.1.
 *
 * L'archivage était sans retour : aucune route ne remettait `active`, et un
 * gabarit archivé refuse ensuite toute nouvelle version et toute publication.
 * Le courtier pilote a archivé le sien par erreur et son dossier est resté
 * bloqué. Le geste inverse existe maintenant, et il est gardé comme l'archivage.
 */
export function useRestoreFormTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<FormTemplateRow>(`/v1/form-templates/${id}/restore`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["form-templates"] });
    },
  });
}

// --- Versions (F2-Forms-1c-ii éditeur) ------------------------------------

/** GET d'une version précise (schema + published_at + template_id). */
export function useFormVersion(versionId: string | undefined) {
  return useQuery({
    queryKey: ["form-versions", "detail", versionId],
    queryFn: () => api.get<FormTemplateVersionRow>(`/v1/form-template-versions/${versionId}`),
    enabled: !!versionId,
  });
}

/**
 * PATCH du `schema` d'un brouillon avec l'empreinte `expected_revision` lue (aucun changelog en
 * 1c-ii — repoussé à la publication 1c-iii). Le contrat draft (permissif) est
 * validé côté client avant l'appel. Invalide la version + la fiche template.
 */
export function usePatchFormVersion(versionId: string, templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { schema: unknown; expected_revision: string }) =>
      api.patch<FormTemplateVersionRow>(`/v1/form-template-versions/${versionId}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["form-versions", "detail", versionId] });
      void qc.invalidateQueries({ queryKey: ["form-templates", "detail", templateId] });
      void qc.invalidateQueries({ queryKey: ["form-templates", "versions", templateId] });
    },
  });
}

/**
 * Publication atomique (1c-iii) : POST /publish avec `{ changelog? }` — une seule
 * intention métier (changelog + published_at + published_by dans la même tx serveur).
 * Invalide version détail + fiche template + liste des versions + liste des templates
 * (l'état de version y change).
 */
export function usePublishFormVersion(versionId: string, templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (changelog: string | null) =>
      api.post<FormTemplateVersionRow>(`/v1/form-template-versions/${versionId}/publish`, {
        ...(changelog !== null ? { changelog } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["form-versions", "detail", versionId] });
      void qc.invalidateQueries({ queryKey: ["form-templates", "detail", templateId] });
      void qc.invalidateQueries({ queryKey: ["form-templates", "versions", templateId] });
      void qc.invalidateQueries({ queryKey: ["form-templates", "list"] });
    },
  });
}

/**
 * Création d'une nouvelle version (1c-iii) : POST /:id/versions (copie le dernier
 * schéma publié, MAX+1). 409 `FORM_VERSION_ACTIVE_DRAFT_EXISTS` (`details.draft_version_id`)
 * géré côté appelant (redirection vers le brouillon existant, pas d'erreur bloquante).
 */
export function useCreateFormVersion(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<FormTemplateVersionRow>(`/v1/form-templates/${templateId}/versions`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["form-templates", "detail", templateId] });
      void qc.invalidateQueries({ queryKey: ["form-templates", "versions", templateId] });
      void qc.invalidateQueries({ queryKey: ["form-templates", "list"] });
    },
  });
}

// ---------------------------------------------------------------------------
// 9.U.3 — génération d'un modèle depuis un document (IA)
// ---------------------------------------------------------------------------

/** Corps du POST /generate-from-document (server-authoritative pour le reste). */
export type GenerateFormTemplateInput = {
  source_file_id: string;
  /** Clé d'idempotence : stable pour toute la session de dialogue. */
  generation_request_id: string;
  name?: string;
  default_language?: "fr" | "en";
};

/**
 * Résultat : le couple (modèle, version BROUILLON) créé atomiquement.
 * `idempotent: true` = rejeu d'une demande identique (aucune nouvelle génération).
 */
export type GenerateFormTemplateResult = {
  template: FormTemplateRow;
  draft_version: FormTemplateVersionRow;
  idempotent: boolean;
};

/**
 * POST /v1/form-templates/generate-from-document. Invalide la liste des modèles
 * au succès ; la navigation vers l'éditeur du brouillon est déclenchée par l'UI.
 * Aucun retry automatique (l'appel est coûteux et l'idempotence est portée par
 * `generation_request_id`, réémis à l'identique par un retry utilisateur).
 */
export function useGenerateFormTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateFormTemplateInput) =>
      api.post<GenerateFormTemplateResult>(
        "/v1/form-templates/generate-from-document",
        input as unknown as Record<string, unknown>,
      ),
    retry: false,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["form-templates"] });
    },
  });
}
